/**
 * Souq Crypto — Analytics Utility
 *
 * Central point for all tracking calls. Rules:
 *  - Never throws; all errors are caught and swallowed so tracking never
 *    blocks a user action.
 *  - Admin and merchant-portal routes are excluded from all tracking.
 *  - GA4 (gtag) events are only fired when cookie consent has been granted.
 *  - The four key conversion events are flagged so GA4 can mark them.
 */

// ─── Global type declarations ─────────────────────────────────────────────────

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clarity: (...args: any[]) => void;
  }
}

// ─── Consent helpers ──────────────────────────────────────────────────────────

const CONSENT_KEY = "analytics_consent";

export type ConsentChoice = "accepted" | "declined";

export function getConsentChoice(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw === "accepted" || raw === "declined") return raw;
    return null;
  } catch {
    return null;
  }
}

export function setConsentChoice(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch { /* ignore */ }
}

export function hasConsent(): boolean {
  return getConsentChoice() === "accepted";
}

// ─── Route guard ─────────────────────────────────────────────────────────────

const EXCLUDED_PATHS = ["/admin", "/merchant-portal"];

function isExcludedRoute(): boolean {
  try {
    const path = window.location.pathname;
    return EXCLUDED_PATHS.some((p) => path === p || path.startsWith(p + "/"));
  } catch {
    return false;
  }
}

// ─── Conversion events ───────────────────────────────────────────────────────

const CONVERSION_EVENTS = new Set([
  "whatsapp_clicked",
  "phone_clicked",
  "inquiry_submitted",
  "merchant_application_submitted",
]);

// ─── Core event dispatcher ────────────────────────────────────────────────────

export interface AnalyticsParams {
  merchant_id?: number | string;
  category?: string;
  city?: string;
  language?: string;
  currency?: string;
  listing_plan?: string;
  traffic_source?: string;
  search_term?: string;
  [key: string]: string | number | boolean | undefined;
}

export function trackEvent(name: string, params?: AnalyticsParams): void {
  try {
    if (isExcludedRoute()) return;
    if (!hasConsent()) return;

    const payload: Record<string, unknown> = { ...params };
    if (CONVERSION_EVENTS.has(name)) {
      payload.conversion = true;
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", name, payload);
    }
  } catch {
    // never propagate tracking errors
  }
}

// ─── Named event helpers ──────────────────────────────────────────────────────

export function trackPageView(path?: string): void {
  try {
    if (isExcludedRoute()) return;
    if (!hasConsent()) return;
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path ?? window.location.pathname,
      });
    }
  } catch { /* ignore */ }
}

export function trackSearchPerformed(params: {
  search_term?: string;
  category?: string;
  result_count?: number;
}): void {
  trackEvent("search_performed", {
    search_term: params.search_term,
    category: params.category,
  });
}

export function trackSearchNoResults(params: { search_term?: string; category?: string }): void {
  trackEvent("search_no_results", {
    search_term: params.search_term,
    category: params.category,
  });
}

export function trackCategoryViewed(params: { category: string; merchant_id?: number }): void {
  trackEvent("category_viewed", { category: params.category });
}

export function trackListingViewed(params: {
  merchant_id?: number;
  category?: string;
  city?: string;
  listing_plan?: string;
}): void {
  trackEvent("listing_viewed", params);
}

export function trackDealViewed(params: {
  merchant_id?: number;
  category?: string;
  city?: string;
}): void {
  trackEvent("deal_viewed", params);
}

export function trackWhatsAppClicked(params: { merchant_id?: number; category?: string }): void {
  trackEvent("whatsapp_clicked", params);
}

export function trackPhoneClicked(params: { merchant_id?: number; category?: string }): void {
  trackEvent("phone_clicked", params);
}

export function trackWebsiteClicked(params: { merchant_id?: number; category?: string }): void {
  trackEvent("website_clicked", params);
}

export function trackDirectionsClicked(params: { merchant_id?: number; city?: string }): void {
  trackEvent("directions_clicked", { ...params, city: params.city ?? "Dubai" });
}

export function trackInquiryStarted(params: { merchant_id?: number; category?: string }): void {
  trackEvent("inquiry_started", params);
}

export function trackInquirySubmitted(params: { merchant_id?: number; category?: string }): void {
  trackEvent("inquiry_submitted", params);
}

export function trackMerchantApplicationStarted(params?: { listing_plan?: string }): void {
  trackEvent("merchant_application_started", params);
}

export function trackMerchantApplicationSubmitted(params?: { listing_plan?: string }): void {
  trackEvent("merchant_application_submitted", params);
}

export function trackLanguageChanged(language: string): void {
  trackEvent("language_changed", { language });
}

export function trackLocationPermissionAllowed(): void {
  trackEvent("location_permission_allowed");
}

export function trackLocationPermissionDenied(): void {
  trackEvent("location_permission_denied");
}
